import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, XCircle, AlertCircle, Sparkles, Clock } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'
import { SeverityBadge } from '@/components/SeverityBadge'
import { useRunStream, type RunStreamEvent } from '@/lib/run-stream'

interface RuleResult {
  id: string
  ruleId: string
  severity: string
  scope: string
  url: string | null
  pass: boolean
  score: number
  evidence: Record<string, unknown> | null
}
interface ProbeResult {
  id: string
  engineId: string
  query: string
  cited: boolean
  position: number | null
  snippet: string | null
  costCents: number
}
interface Fix {
  id: string
  ruleId: string
  status: string
  mode: string
}
interface Run {
  id: string
  status: string
  score: number | null
  pagesCrawled: number
  probesRun: number
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
  ruleResults: RuleResult[]
  probeResults: ProbeResult[]
  fixes: Fix[]
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

export function RunDetailPage() {
  const t = useT()
  const qc = useQueryClient()
  const { siteId = '', runId = '' } = useParams()
  const [recent, setRecent] = useState<RunStreamEvent[]>([])

  const run = useQuery({
    queryKey: ['run', siteId, runId],
    queryFn: () => apiClient.get<Run>(`/sites/${siteId}/runs/${runId}`),
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'queued' || s === 'running' ? 3_000 : false
    },
  })

  useRunStream(siteId, runId, (e) => {
    setRecent((r) => [e, ...r].slice(0, 30))
    if (e.kind === 'completed' || e.kind === 'failed' || e.kind === 'evaluated' || e.kind === 'probed' || e.kind === 'crawled') {
      qc.invalidateQueries({ queryKey: ['run', siteId, runId] })
    }
  })

  if (run.isLoading) return <p>{t('common.loading')}</p>
  if (run.isError || !run.data) return <p>{t('common.error')}</p>

  const r = run.data
  const failing = [...r.ruleResults]
    .filter((x) => !x.pass)
    .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9))
  const passing = r.ruleResults.filter((x) => x.pass)
  const sevCount = failing.reduce<Record<string, number>>((acc, f) => {
    acc[f.severity] = (acc[f.severity] ?? 0) + 1
    return acc
  }, {})
  const citedCount = r.probeResults.filter((p) => p.cited).length
  const totalProbeCost = r.probeResults.reduce((s, p) => s + (p.costCents ?? 0), 0)
  const scoreColor = r.score === null ? 'opacity-60' : r.score >= 70 ? 'text-emerald-500' : r.score >= 40 ? 'text-amber-500' : 'text-red-500'

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">{t('run.title', { id: r.id.slice(0, 8) })}</h1>
      <p className="text-sm opacity-70 mb-4">
        {t(`run.status.${r.status}`)} · {new Date(r.createdAt).toLocaleString()}
      </p>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('site.detail.score')}</div>
          <div className={`text-3xl font-semibold mt-1 ${scoreColor}`}>{r.score ?? '—'}</div>
        </div>
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('run.summary.findings')}</div>
          <div className="text-3xl font-semibold mt-1">{failing.length}</div>
          <div className="text-xs opacity-60 mt-0.5">{passing.length} {t('run.summary.passing')}</div>
        </div>
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('run.summary.pages')}</div>
          <div className="text-3xl font-semibold mt-1">{r.pagesCrawled}</div>
          {r.pagesCrawled === 0 && <div className="text-xs opacity-60 mt-0.5">{t('run.summary.pages.none')}</div>}
        </div>
        <div className="p-4 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs opacity-70">{t('run.summary.cited')}</div>
          <div className="text-3xl font-semibold mt-1">{citedCount}/{r.probesRun}</div>
          <div className="text-xs opacity-60 mt-0.5">${(totalProbeCost / 100).toFixed(2)}</div>
        </div>
      </section>

      {r.errorMessage && (
        <section className="mb-4 p-3 rounded border flex items-start gap-2" style={{ borderColor: '#dc2626', background: 'rgba(220,38,38,0.05)' }}>
          <AlertCircle className="w-4 h-4 mt-0.5" style={{ color: '#dc2626' }} />
          <div className="text-sm">{r.errorMessage}</div>
        </section>
      )}

      {r.pagesCrawled === 0 && r.status === 'completed' && (
        <section className="mb-4 p-3 rounded border flex items-start gap-2" style={{ borderColor: 'var(--color-border)', background: 'rgba(245,158,11,0.05)' }}>
          <AlertCircle className="w-4 h-4 mt-0.5" style={{ color: '#d97706' }} />
          <div className="text-sm">
            <strong>{t('run.crawl.zero.title')}</strong>
            <div className="opacity-80 mt-1">{t('run.crawl.zero.body')}</div>
          </div>
        </section>
      )}

      {recent.length > 0 && (r.status === 'running' || r.status === 'queued') && (
        <section className="mb-6 p-3 rounded border" style={{ borderColor: 'var(--color-border)' }}>
          <div className="text-xs font-medium opacity-70 mb-2 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {t('run.live')}</div>
          <ul className="space-y-1 text-xs">
            {recent.slice(0, 8).map((e, i) => (
              <li key={i}>
                <span className="opacity-50">{new Date('ts' in e ? e.ts : Date.now()).toLocaleTimeString()}</span>{' '}
                <span className="opacity-80">{renderEvent(e, t)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-sm font-medium opacity-80 mb-2">
          {t('run.findings')} ({failing.length})
          {failing.length > 0 && (
            <span className="ml-3 text-xs opacity-70">
              {(['critical', 'high', 'medium', 'low'] as const).map(
                (s) => sevCount[s] ? `${sevCount[s]} ${t('severity.' + s).toLowerCase()}` : null,
              ).filter(Boolean).join(' · ')}
            </span>
          )}
        </h2>
        {failing.length === 0 && <p className="opacity-70 text-sm">{t('run.findings.empty')}</p>}
        {failing.length > 0 && (
          <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {failing.map((rule) => (
              <li key={rule.id} className="py-3 flex items-start gap-3">
                <SeverityBadge severity={rule.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{rule.ruleId}</div>
                  {rule.url && <div className="text-xs opacity-60 break-all">{rule.url}</div>}
                  {rule.evidence && (
                    <div className="text-xs opacity-80 mt-1 font-mono break-all">
                      {formatEvidence(rule.evidence)}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {passing.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-medium opacity-80 mb-2">
            {t('run.passing')} ({passing.length})
          </h2>
          <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {passing.map((rule) => (
              <li key={rule.id} className="py-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                <div className="text-sm">{rule.ruleId}</div>
                {rule.url && <div className="text-xs opacity-50 truncate">{rule.url}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-sm font-medium opacity-80 mb-2">{t('run.probes')}</h2>
        {r.probeResults.length === 0 && <p className="opacity-70 text-sm">{t('run.probes.empty')}</p>}
        {r.probeResults.length > 0 && (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left opacity-70">
                <th className="py-2 pr-4">{t('run.probes.col.engine')}</th>
                <th className="py-2 pr-4">{t('run.probes.col.query')}</th>
                <th className="py-2 pr-4">{t('run.probes.col.cited')}</th>
                <th className="py-2 pr-4">{t('run.probes.col.position')}</th>
                <th className="py-2 pr-4">{t('run.probes.col.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {r.probeResults.map((p) => (
                <tr key={p.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="py-2 pr-4 font-medium">{p.engineId}</td>
                  <td className="py-2 pr-4 opacity-80">{p.query}</td>
                  <td className="py-2 pr-4">
                    {p.cited
                      ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-accent)' }} /> {t('run.probes.cited.yes')}</span>
                      : <span className="inline-flex items-center gap-1 opacity-60"><XCircle className="w-4 h-4" /> {t('run.probes.cited.no')}</span>}
                  </td>
                  <td className="py-2 pr-4">{p.position ?? '—'}</td>
                  <td className="py-2 pr-4 opacity-70">${((p.costCents ?? 0) / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {r.probeResults.length > 0 && totalProbeCost === 0 && (
          <p className="text-xs opacity-60 mt-2">{t('run.probes.stub')}</p>
        )}
      </section>

      <section>
        <h2 className="text-sm font-medium opacity-80 mb-2 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4" /> {t('run.fixes')}
        </h2>
        {r.fixes.length === 0 ? (
          <p className="opacity-70 text-sm">{t('run.fixes.empty')}</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {r.fixes.map((f) => (
              <li key={f.id} className="py-2 flex items-center justify-between">
                <Link to={`/fixes/${f.id}`} className="text-sm hover:underline">{f.ruleId}</Link>
                <span className="text-xs opacity-70">{f.mode} · {f.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

function formatEvidence(ev: Record<string, unknown>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(ev)) {
    if (v === null || v === undefined) continue
    if (Array.isArray(v)) {
      parts.push(`${k}=[${v.slice(0, 5).map(String).join(', ')}${v.length > 5 ? '…' : ''}]`)
    } else if (typeof v === 'object') {
      parts.push(`${k}=${JSON.stringify(v).slice(0, 80)}`)
    } else {
      const s = String(v)
      parts.push(`${k}=${s.length > 80 ? s.slice(0, 80) + '…' : s}`)
    }
  }
  return parts.join(' · ')
}

function renderEvent(e: RunStreamEvent, t: (k: string, vars?: Record<string, unknown>) => string): string {
  switch (e.kind) {
    case 'started':   return t('run.event.started')
    case 'crawled':   return t('run.event.crawled', { n: e.pagesCrawled })
    case 'evaluated': return t('run.event.evaluated', { pass: e.rulesPassed, fail: e.rulesFailed })
    case 'probed':    return `${e.engineId}: ${e.cited ? t('run.event.probed.cited') : t('run.event.probed.notcited')} — ${e.query}`
    case 'fix':       return `${t('run.event.fix')} ${e.fixId.slice(0, 8)} ${e.status} (${e.ruleId})`
    case 'completed': return t('run.event.completed', { score: e.score ?? '—' })
    case 'failed':    return t('run.event.failed', { error: e.error })
    case 'hello':     return ''
  }
}
